/** Races overview — grid of championship season cards */
import { useState, useEffect, useCallback } from 'react';
import { rpc } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { Trophy, Calendar, Flag } from 'lucide-react';
import type { ListChampionshipsResponse, RaceChampionshipSummary } from '@/types/telemetry';

export function RacesOverview() {
  const { navigate } = useRouter();
  const { t } = useI18n();

  const [data, setData] = useState<ListChampionshipsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await rpc<ListChampionshipsResponse>('ListChampionships');
      setData(res);
    } catch (e: any) {
      setError(e.message || t('races.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} />;
  if (!data || !data.championships?.length) return <ErrorState message={t('races.noData')} />;

  // Sort newest first
  const sorted = [...data.championships].sort((a, b) => b.year - a.year);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Trophy size={18} className="text-accent" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('races.title')}</h1>
          <p className="text-xs text-text-muted mt-0.5">{t('races.seasons')}</p>
        </div>
      </div>

      {/* Season grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sorted.map((champ) => (
          <SeasonCard
            key={champ.id}
            championship={champ}
            onClick={() => navigate(`#/races/${champ.id}`)}
          />
        ))}
      </div>
    </div>
  );
}

function SeasonCard({ championship, onClick }: { championship: RaceChampionshipSummary; onClick: () => void }) {
  const { t } = useI18n();

  return (
    <button
      onClick={onClick}
      className="card-interactive text-left p-5 group hover:border-border-hover hover:bg-bg-hover w-full"
    >
      {/* Year accent */}
      <div className="flex items-start justify-between mb-3">
        <span className="text-2xl font-bold gradient-text">{championship.year}</span>
        <span className="text-xs text-text-muted uppercase tracking-wider font-medium mt-1">
          {championship.country}
        </span>
      </div>

      {/* Name */}
      <h3 className="text-sm font-semibold text-text-primary mb-3 leading-snug">
        {championship.name}
      </h3>

      {/* Stats */}
      <div className="flex items-center gap-4 text-xs text-text-secondary">
        <span className="flex items-center gap-1.5">
          <Calendar size={12} className="text-text-muted" />
          {t('races.events', { n: championship.eventCount })}
        </span>
        <span className="flex items-center gap-1.5">
          <Flag size={12} className="text-text-muted" />
          {t('races.categories', { n: championship.categoryCount })}
        </span>
      </div>

      {/* Bottom accent line */}
      <div className="mt-4 h-[2px] rounded-full bg-gradient-to-r from-accent/40 via-purple/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}
